import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { numberAttribute, primitiveStyles } from '../shared/primitive-utils.js';

/**
 * Determinate or indeterminate progress. Omitting value means indeterminate.
 * @element qds-progress
 * @attr {number} value - Finite progress from 0 to max; clamped to the range.
 * @attr {number} max - Positive maximum. Default 100.
 * @attr {string} label - Accessible progress name. Default Progress.
 * @attr {string} value-text - Human-readable progress, e.g. 3 of 5 files.
 * @property {number|null} value - null restores indeterminate state.
 * @property {number} max - Positive maximum.
 * @csspart track - Progress track.
 * @csspart indicator - Filled portion, animated only when indeterminate.
 */
export class QdsProgress extends HTMLElement {
  static get observedAttributes(): string[] { return ['value', 'max', 'label', 'value-text']; }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: block; min-width: 4rem; }
      [part="track"] { overflow: hidden; height: 8px; background: var(--theme-border-primary, #e4e9f2); border-radius: 4px; }
      [part="indicator"] { height: 100%; width: 0; background: var(--theme-accent-primary, #445e89); }
      .indeterminate [part="indicator"] { width: 35%; animation: progress 1.5s ease-in-out infinite alternate; }
      @keyframes progress { to { transform: translateX(185%); } }
      @media (prefers-reduced-motion: reduce) { .indeterminate [part="indicator"] { animation: none; width: 100%; opacity: .6; } }
      @media (forced-colors: active) { [part="track"] { border: 1px solid CanvasText; } [part="indicator"] { background: Highlight; } }
    </style><div part="track" role="progressbar" aria-valuemin="0"><div part="indicator"></div></div>`;
    loadBaseCSS();
  }
  connectedCallback(): void { for (const prop of ['value', 'max']) upgradeProperty(this, prop); this._sync(); }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get max(): number { return Math.max(1, numberAttribute(this, 'max', 100)); }
  set max(value: number) { this.setAttribute('max', String(value)); }
  get value(): number | null {
    const raw = numberAttribute(this, 'value', NaN);
    return Number.isFinite(raw) ? Math.max(0, Math.min(this.max, raw)) : null;
  }
  set value(value: number | null) { if (value === null || value === undefined) this.removeAttribute('value'); else this.setAttribute('value', String(value)); }
  private _sync(): void {
    const track = this.shadowRoot!.querySelector('[part="track"]')!;
    const indicator = this.shadowRoot!.querySelector<HTMLElement>('[part="indicator"]')!;
    track.setAttribute('aria-label', this.getAttribute('label') || 'Progress');
    track.setAttribute('aria-valuemax', String(this.max));
    track.classList.toggle('indeterminate', this.value === null);
    if (this.value === null) { track.removeAttribute('aria-valuenow'); indicator.style.removeProperty('width'); }
    else { track.setAttribute('aria-valuenow', String(this.value)); indicator.style.width = `${this.value / this.max * 100}%`; }
    if (this.hasAttribute('value-text')) track.setAttribute('aria-valuetext', this.getAttribute('value-text')!);
    else track.removeAttribute('aria-valuetext');
  }
}
if (!customElements.get('qds-progress')) customElements.define('qds-progress', QdsProgress);
